/**
 * Login Page
 * SCR-001: Multiple authentication methods
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-001 Login
 * @iso27001 A.9.4.2 Secure log-on procedures
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@myvault/shared/hooks';
import { Alert, Spinner } from '@myvault/shared/components';
import { featureFlags } from '@/config/featureFlags';

type AuthMethod = 'email-code' | 'password' | 'magic-link';

interface LoginState {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface EmailCodeState {
  digits: string[];
  maskedEmail: string;
  expiresIn: number;
  attemptsRemaining: number;
}

interface MagicLinkState {
  sent: boolean;
  maskedEmail: string;
  cooldownSeconds: number;
}

const API_BASE = '/api/v1/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokensAndRefresh } = useAuth();

  // Auth method tabs
  const [activeMethod, setActiveMethod] = useState<AuthMethod>('email-code');

  // Form state
  const [formState, setFormState] = useState<LoginState>({
    email: '',
    password: '',
    rememberMe: false,
  });

  // Email code state
  const [emailCodeState, setEmailCodeState] = useState<EmailCodeState>({
    digits: ['', '', '', '', '', ''],
    maskedEmail: '',
    expiresIn: 0,
    attemptsRemaining: 3,
  });
  const [showCodeInput, setShowCodeInput] = useState(false);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Magic link state
  const [magicLinkState, setMagicLinkState] = useState<MagicLinkState>({
    sent: false,
    maskedEmail: '',
    cooldownSeconds: 0,
  });

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Countdown timer for email code
  useEffect(() => {
    if (emailCodeState.expiresIn > 0) {
      const timer = setInterval(() => {
        setEmailCodeState((prev) => ({
          ...prev,
          expiresIn: Math.max(0, prev.expiresIn - 1),
        }));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [emailCodeState.expiresIn]);

  // Cooldown timer for magic link resend
  useEffect(() => {
    if (magicLinkState.cooldownSeconds > 0) {
      const timer = setInterval(() => {
        setMagicLinkState((prev) => ({
          ...prev,
          cooldownSeconds: Math.max(0, prev.cooldownSeconds - 1),
        }));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [magicLinkState.cooldownSeconds]);

  // ============= API Mutations =============

  // Password login
  const passwordLoginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string; rememberMe: boolean }) => {
      const response = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          remember_me: data.rememberMe,
        }),
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ongeldige inloggegevens');
      }
      return response.json();
    },
    onSuccess: async (data) => {
      // Store tokens and refresh user data
      // API returns camelCase (accessToken/refreshToken)
      const accessToken = data.accessToken || data.access_token;
      const refreshToken = data.refreshToken || data.refresh_token;
      if (accessToken && refreshToken) {
        await setTokensAndRefresh(accessToken, refreshToken);
      }
      const redirectUrl = searchParams.get('redirect') || '/dashboard';
      navigate(redirectUrl);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Request email code
  const requestEmailCodeMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await fetch(`${API_BASE}/email-code/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          throw new Error('Te veel verzoeken. Probeer over 15 minuten opnieuw.');
        }
        throw new Error(errorData.message || 'Kan geen code versturen');
      }
      return response.json();
    },
    onSuccess: (data) => {
      setEmailCodeState({
        digits: ['', '', '', '', '', ''],
        maskedEmail: data.masked_email || data.maskedEmail,
        expiresIn: data.expires_in || data.expiresIn || 600,
        attemptsRemaining: 3,
      });
      setShowCodeInput(true);
      setError(null);
      // Focus first code input
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Verify email code
  const verifyEmailCodeMutation = useMutation({
    mutationFn: async (data: { email: string; code: string }) => {
      const response = await fetch(`${API_BASE}/email-code/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, code: data.code }),
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.attempts_remaining !== undefined) {
          setEmailCodeState((prev) => ({
            ...prev,
            attemptsRemaining: errorData.attempts_remaining || errorData.attemptsRemaining,
            digits: ['', '', '', '', '', ''],
          }));
          codeInputRefs.current[0]?.focus();
        }
        throw new Error(errorData.message || 'Ongeldige of verlopen code');
      }
      return response.json();
    },
    onSuccess: async (data) => {
      // Store tokens and refresh user data
      // API returns camelCase (accessToken/refreshToken)
      const accessToken = data.accessToken || data.access_token;
      const refreshToken = data.refreshToken || data.refresh_token;
      if (accessToken && refreshToken) {
        await setTokensAndRefresh(accessToken, refreshToken);
      }
      const redirectUrl = searchParams.get('redirect') || '/dashboard';
      navigate(redirectUrl);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Request magic link
  const requestMagicLinkMutation = useMutation({
    mutationFn: async (email: string) => {
      const redirectUrl = searchParams.get('redirect') || '/dashboard';
      const response = await fetch(`${API_BASE}/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, redirect_url: redirectUrl }),
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          throw new Error('Te veel verzoeken. Probeer over 15 minuten opnieuw.');
        }
        throw new Error(errorData.message || 'Kan geen link versturen');
      }
      return response.json();
    },
    onSuccess: (data) => {
      setMagicLinkState({
        sent: true,
        maskedEmail: data.masked_email || data.maskedEmail,
        cooldownSeconds: 60,
      });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // ============= Event Handlers =============

  const handleOAuthLogin = (provider: 'google' | 'digid') => {
    const redirectUrl = searchParams.get('redirect') || '/dashboard';
    window.location.href = `${API_BASE}/oauth/${provider}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    passwordLoginMutation.mutate({
      email: formState.email,
      password: formState.password,
      rememberMe: formState.rememberMe,
    });
  };

  const handleEmailCodeRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    requestEmailCodeMutation.mutate(formState.email);
  };

  const handleCodeDigitChange = useCallback(
    (index: number, value: string) => {
      // Only allow digits
      if (value && !/^\d$/.test(value)) return;

      const newDigits = [...emailCodeState.digits];
      newDigits[index] = value;
      setEmailCodeState((prev) => ({ ...prev, digits: newDigits }));

      // Auto-focus next input
      if (value && index < 5) {
        codeInputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when all digits entered
      if (value && index === 5 && newDigits.every((d) => d !== '')) {
        const code = newDigits.join('');
        verifyEmailCodeMutation.mutate({ email: formState.email, code });
      }
    },
    [emailCodeState.digits, formState.email, verifyEmailCodeMutation]
  );

  const handleCodePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      if (pastedData.length === 6) {
        const newDigits = pastedData.split('');
        setEmailCodeState((prev) => ({ ...prev, digits: newDigits }));
        codeInputRefs.current[5]?.focus();
        // Auto-submit
        verifyEmailCodeMutation.mutate({ email: formState.email, code: pastedData });
      }
    },
    [formState.email, verifyEmailCodeMutation]
  );

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !emailCodeState.digits[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleMagicLinkRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    requestMagicLinkMutation.mutate(formState.email);
  };

  const handleResendCode = () => {
    setError(null);
    requestEmailCodeMutation.mutate(formState.email);
  };

  const handleResendMagicLink = () => {
    if (magicLinkState.cooldownSeconds > 0) return;
    setError(null);
    requestMagicLinkMutation.mutate(formState.email);
  };

  const handleBackToEmail = () => {
    setShowCodeInput(false);
    setMagicLinkState({ sent: false, maskedEmail: '', cooldownSeconds: 0 });
    setError(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isLoading =
    passwordLoginMutation.isPending ||
    requestEmailCodeMutation.isPending ||
    verifyEmailCodeMutation.isPending ||
    requestMagicLinkMutation.isPending;

  // ============= Render Methods =============

  const renderEmailCodeVerification = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <svg
            className="h-8 w-8 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Controleer je inbox</h2>
        <p className="mt-2 text-sm text-gray-600">
          We hebben een 6-cijferige code gestuurd naar{' '}
          <strong>{emailCodeState.maskedEmail}</strong>
        </p>
      </div>

      {/* Code Input */}
      <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
        {emailCodeState.digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (codeInputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleCodeKeyDown(index, e)}
            disabled={isLoading}
            className="h-14 w-12 rounded-lg border border-gray-300 text-center text-2xl font-semibold focus:border-blue-600 focus:ring-2 focus:ring-blue-600 disabled:bg-gray-50"
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      {/* Timer and attempts */}
      <div className="text-center text-sm text-gray-600">
        {emailCodeState.expiresIn > 0 ? (
          <p>Code verloopt over {formatTime(emailCodeState.expiresIn)}</p>
        ) : (
          <p className="text-red-600">Code is verlopen</p>
        )}
        {emailCodeState.attemptsRemaining < 3 && (
          <p className="mt-1 text-amber-600">
            Nog {emailCodeState.attemptsRemaining} pogingen over
          </p>
        )}
      </div>

      {/* Resend link */}
      <div className="text-center">
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isLoading}
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
        >
          Code niet ontvangen? Opnieuw versturen
        </button>
      </div>

      {/* Back link */}
      <button
        type="button"
        onClick={handleBackToEmail}
        className="flex w-full items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Andere methode gebruiken
      </button>
    </div>
  );

  const renderMagicLinkSent = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Inloglink verzonden!</h2>
        <p className="mt-2 text-sm text-gray-600">
          We hebben een inloglink gestuurd naar <strong>{magicLinkState.maskedEmail}</strong>
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Klik op de link in de email om in te loggen. De link is 15 minuten geldig.
        </p>
      </div>

      {/* Email client hints */}
      <div className="rounded-lg bg-gray-50 p-4">
        <p className="text-sm text-gray-600">
          Controleer ook je spam folder als je de email niet binnen enkele minuten ontvangt.
        </p>
      </div>

      {/* Resend button */}
      <button
        type="button"
        onClick={handleResendMagicLink}
        disabled={isLoading || magicLinkState.cooldownSeconds > 0}
        className="w-full rounded-lg border border-gray-300 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {magicLinkState.cooldownSeconds > 0
          ? `Opnieuw versturen (${magicLinkState.cooldownSeconds}s)`
          : 'Opnieuw versturen'}
      </button>

      {/* Back link */}
      <button
        type="button"
        onClick={handleBackToEmail}
        className="flex w-full items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Andere methode gebruiken
      </button>
    </div>
  );

  const renderAuthForm = () => {
    // If showing code verification
    if (activeMethod === 'email-code' && showCodeInput) {
      return renderEmailCodeVerification();
    }

    // If magic link was sent
    if (activeMethod === 'magic-link' && magicLinkState.sent) {
      return renderMagicLinkSent();
    }

    return (
      <>
        {/* Auth Method Tabs */}
        <div className="mb-6 flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          {[
            { id: 'email-code', label: 'Email code' },
            { id: 'password', label: 'Wachtwoord' },
            { id: 'magic-link', label: 'Inloglink' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveMethod(tab.id as AuthMethod);
                setError(null);
              }}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                activeMethod === tab.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Email input (shared) */}
        <div className="mb-4">
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            E-mailadres
          </label>
          <input
            type="email"
            id="email"
            value={formState.email}
            onChange={(e) => setFormState((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="jouw@email.nl"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:border-blue-600 focus:ring-2 focus:ring-blue-600"
            required
            autoComplete="email"
          />
        </div>

        {/* Method-specific fields */}
        {activeMethod === 'password' && (
          <>
            <div className="mb-4">
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
                Wachtwoord
              </label>
              <input
                type="password"
                id="password"
                value={formState.password}
                onChange={(e) => setFormState((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••••••"
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:border-blue-600 focus:ring-2 focus:ring-blue-600"
                required
                autoComplete="current-password"
              />
            </div>

            <div className="mb-6 flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={formState.rememberMe}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, rememberMe: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                />
                <span className="text-sm text-gray-600">Onthoud mij</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Wachtwoord vergeten?
              </Link>
            </div>
          </>
        )}

        {/* Submit button */}
        <button
          type="submit"
          onClick={
            activeMethod === 'email-code'
              ? handleEmailCodeRequest
              : activeMethod === 'password'
              ? handlePasswordSubmit
              : handleMagicLinkRequest
          }
          disabled={isLoading || !formState.email}
          className="w-full rounded-lg bg-blue-600 py-3 font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <Spinner size="sm" className="mx-auto" />
          ) : activeMethod === 'email-code' ? (
            'Verstuur code'
          ) : activeMethod === 'password' ? (
            'Inloggen'
          ) : (
            'Stuur inloglink'
          )}
        </button>
      </>
    );
  };

  return (
    <div>
      <h1 className="mb-2 text-center text-2xl font-bold text-gray-900">Welkom terug</h1>
      <p className="mb-8 text-center text-gray-600">
        Log in op je myVault account
      </p>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" className="mb-6" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Session expired message */}
      {searchParams.get('expired') === 'true' && (
        <Alert variant="warning" className="mb-6">
          Je sessie is verlopen. Log opnieuw in om door te gaan.
        </Alert>
      )}

      {/* OAuth Buttons */}
      {!(showCodeInput || magicLinkState.sent) && (
        <>
          <div className="mb-6 space-y-3">
            <button
              type="button"
              onClick={() => handleOAuthLogin('google')}
              className="flex w-full items-center justify-center gap-3 rounded-lg border-2 border-gray-300 py-3 px-4 font-medium text-gray-700 transition-colors hover:border-blue-600"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Doorgaan met Google
            </button>

            {/* DigiD authentication - controlled by VITE_FEATURE_DIGID env var */}
            {featureFlags.DIGID_ENABLED && (
              <button
                type="button"
                onClick={() => handleOAuthLogin('digid')}
                className="flex w-full items-center justify-center gap-3 rounded-lg border-2 border-gray-300 py-3 px-4 font-medium text-gray-700 transition-colors hover:border-blue-600"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded bg-orange-500 text-xs font-bold text-white">
                  D
                </span>
                Doorgaan met DigiD
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gray-50 px-4 text-gray-500">of</span>
            </div>
          </div>
        </>
      )}

      {/* Auth Form */}
      <form onSubmit={(e) => e.preventDefault()}>{renderAuthForm()}</form>

      {/* Register link */}
      {!(showCodeInput || magicLinkState.sent) && (
        <p className="mt-8 text-center text-sm text-gray-600">
          Nog geen account?{' '}
          <Link to="/register" className="font-medium text-blue-600 hover:text-blue-800">
            Registreer gratis
          </Link>
        </p>
      )}
    </div>
  );
}

export default LoginPage;
